import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	createAiManagedObject,
	fetchAiManagedObject,
	updateAiManagedObject,
} from '@atlassian/ai-managed-objects/api';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';
import { validateManagedBlockConfig } from '@atlassian/ai-managed-objects/validation';
import type { ConfluencePageAri } from '@atlassian/ari';

import type { AgentManagedExtensionAttributes } from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';
import { getNodeAiManagedObject } from '../../common/utils';

import {
	dispatchAgentManagedExtensionMeta,
	findConfiguredAgentManagedExtension,
	setAgentManagedExtensionMeta,
	setNodeAiManagedObject,
} from './agentManagedObjectNodeMetadata';

type UseAiManagedObjectArgs = {
	aimoId?: string;
	cachedAimo?: AiManagedObject;
	configLocalId?: string;
	editorView: EditorView;
	foundPos?: number;
	isOpen: boolean;
	referenceObjectAri?: ConfluencePageAri;
};

type UseAiManagedObjectResult = {
	error: string | null;
	handleCancel: () => void;
	handleSave: (prompt: string) => Promise<void>;
	initialPrompt: string;
	isSaving: boolean;
};

export const useAiManagedObject = ({
	aimoId,
	cachedAimo,
	configLocalId,
	editorView,
	foundPos,
	isOpen,
	referenceObjectAri,
}: UseAiManagedObjectArgs): UseAiManagedObjectResult => {
	const intl = useIntl();
	const fetchErrorMessage = intl.formatMessage(messages.fetchError);
	const saveErrorMessage = intl.formatMessage(messages.saveError);
	const [aimoData, setAimoData] = useState<AiManagedObject | null>(cachedAimo ?? null);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!isOpen || foundPos === undefined) {
			setAimoData(null);
			setError(null);
			setIsSaving(false);
			return;
		}

		if (!aimoId) {
			setAimoData(null);
			setError(null);
			return;
		}

		if (cachedAimo) {
			setAimoData(cachedAimo);
			setError(null);
			return;
		}

		let cancelled = false;
		setError(null);
		fetchAiManagedObject(aimoId)
			.then((aimo) => {
				if (!cancelled) {
					setAimoData(aimo);
					if (aimo) {
						dispatchAgentManagedExtensionMeta(editorView, {
							aiManagedObject: aimo,
							type: 'CACHE_AI_MANAGED_OBJECT',
						});
					} else {
						setError(fetchErrorMessage);
					}
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAimoData(null);
					setError(fetchErrorMessage);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [aimoId, cachedAimo, editorView, fetchErrorMessage, foundPos, isOpen]);

	const handleSave = useCallback(
		async (prompt: string): Promise<void> => {
			const validation = validateManagedBlockConfig({ prompt });
			if (!validation.valid) {
				setError(validation.errors[0]?.message ?? saveErrorMessage);
				return;
			}

			setIsSaving(true);
			setError(null);

			try {
				const currentFound = findConfiguredAgentManagedExtension(editorView, configLocalId);
				if (!currentFound) {
					setError(saveErrorMessage);
					setIsSaving(false);
					return;
				}

				const attrs = currentFound.node.attrs as AgentManagedExtensionAttributes;
				const currentId = getNodeAiManagedObject(attrs)?.id;
				const localId = currentFound.node.attrs.localId as string | undefined;
				const config = { prompt };
				let aimo: AiManagedObject;

				if (currentId) {
					aimo = await updateAiManagedObject({
						id: currentId,
						config,
					});
				} else if (referenceObjectAri) {
					aimo = await createAiManagedObject({
						cloudId: referenceObjectAri.siteId,
						referenceObjectAri: referenceObjectAri.toString(),
						referenceProduct: 'CONFLUENCE',
						status: 'ACTIVE',
						type: 'BLOCK',
						config,
					});
				} else {
					setError(saveErrorMessage);
					setIsSaving(false);
					return;
				}

				const tr = editorView.state.tr;
				setNodeAiManagedObject(editorView.state, tr, aimo, localId);
				setAgentManagedExtensionMeta(tr, [
					{
						aiManagedObject: aimo,
						type: 'CACHE_AI_MANAGED_OBJECT',
					},
					{ type: 'CLOSE_CONFIG' },
				]);
				editorView.dispatch(tr);
				setAimoData(aimo);
				setIsSaving(false);
			} catch {
				setError(saveErrorMessage);
				setIsSaving(false);
			}
		},
		[configLocalId, editorView, referenceObjectAri, saveErrorMessage],
	);

	const handleCancel = useCallback((): void => {
		dispatchAgentManagedExtensionMeta(editorView, { type: 'CLOSE_CONFIG' });
	}, [editorView]);

	return {
		error: error,
		handleCancel: handleCancel,
		handleSave: handleSave,
		initialPrompt: aimoData?.config?.prompt ?? '',
		isSaving: isSaving,
	};
};
